// src/components/desk/AgentFacetList.jsx — THE agent facet, and the two pieces it is drawn
// from. Lifted out of IntakeTab on 2026-09-11 because the contracts tab needs the SAME control:
// owner — "only admin sees list like it sees in desk filter, you can import it exactly."
//
// 🔑 IMPORTED, NOT COPIED. A second agent list would be a second thing to keep in step with the
// roster, the counts, the resigned-agent rule and the Georgian↔Latin search — and this repo has
// paid for a copied definition often enough to have a rule about it. IntakeTab imports these
// three from here now; the bytes below are its own, moved rather than retyped.
//
// The behaviour, unchanged: ACTIVE agents only by default (the roster carries ~76 people and
// ~20 are current), resigned one click away and never gone, search spanning BOTH sets because
// typing a name is an explicit request for that person, and `normalizeText` matching so
// "gvantsa" finds გვანცა.
import { useMemo, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { normalizeText, UNNORMALIZABLE } from '../../../core/normalization.js'

const AGENT_FACET_TOP_N = 8

export function TriGlyph({ state, dead = false, inherited = false }) {
  // B1216: an INHERITED state (a child under a decided ancestor) is the same glyph drawn lighter — the state is
  // real (the search delivers it) but the agent did not press this row; a press here makes an EXCEPTION.
  const base = 'h-4 w-4 shrink-0 rounded border text-[11px] leading-none flex items-center justify-center' + (inherited ? ' opacity-50' : '')
  if (state === 'exclude') return <span aria-hidden="true" className={`${base} border-rose-500 text-rose-600 dark:text-rose-400`}>✕</span>
  if (state === 'include') return <span aria-hidden="true" className={`${base} border-primary bg-primary text-primary-foreground`}>✓</span>
  if (state === 'mixed') return <span aria-hidden="true" className={`${base} border-primary text-primary`}>−</span>
  return <span aria-hidden="true" className={`${base} ${dead ? 'border-border' : 'border-muted-foreground/60'}`} />
}

export function FacetCheck({ checked, disabled, onChange, onName, label, count, title, indeterminate = false, excluded = false, inherited = false }) {
  // F-050b (owner, 2026-09-06): "pressing on a box must continue to ✕ and pressing on a title must deselect, so if
  // the user wants neutral there is no need to first filter for ✕". The BOX cycles; the NAME is select/deselect
  // (src/lib/facetTriState.js namePress / districtTree.js districtNamePress). Two SIBLING buttons, no <label>:
  // F-050's one-element rule now holds per element, and nothing is swapped under a click.
  const dead = disabled && !checked && !excluded
  // title: the row truncates, so the full name must be reachable on hover (owner, 2026-08-18
  // — "ვაჟა-ფშაველას …" rows in the district tree were unreadable). String labels self-title;
  // node labels (ownBucket's hint span) pass their own `title` or none.
  const hoverTitle = title ?? (typeof label === 'string' ? label : undefined)
  const state = excluded ? 'exclude' : checked ? 'include' : indeterminate ? 'mixed' : null
  const rowCls = `flex w-full items-center gap-2 text-xs text-left ${dead ? 'opacity-40' : 'text-muted-foreground hover:text-foreground'}`
  return (
    <div className={rowCls} data-facet-row="">
      <button type="button" role="checkbox" aria-checked={state === 'include' ? 'true' : state === 'mixed' ? 'mixed' : 'false'}
        data-state={state || 'neutral'} data-inherited={inherited ? 'true' : undefined} disabled={dead} onClick={onChange}
        title={inherited ? t('desk_facet_inherited_hint') : excluded ? t('desk_facet_exclude_hint') : checked ? t('desk_facet_exclude_next') : undefined}
        className={`shrink-0 flex items-center ${dead ? 'cursor-default' : 'cursor-pointer'}`}>
        <TriGlyph state={state} dead={dead} inherited={inherited} />
      </button>
      <button type="button" data-facet-name="" disabled={dead} onClick={onName || onChange}
        title={state ? t('desk_facet_name_deselect_hint') : hoverTitle}
        className={`flex-1 min-w-0 truncate text-left ${dead ? 'cursor-default' : 'cursor-pointer'}`}>{label}</button>
      {count != null && count > 0 && <span className="text-xs text-muted-foreground shrink-0">({count})</span>}
    </div>
  )
}

const agentSearchKey = v => {
  const n = normalizeText(v || '')
  return n === UNNORMALIZABLE ? (v || '').toLowerCase() : n
}
export function AgentFacetList({ agents, selected, onToggle, onName, counts = null, excluded = [] }) {
  const [q, setQ] = useState('')
  const [showAll, setShowAll] = useState(false)        // more ACTIVE rows
  const [withInactive, setWithInactive] = useState(false)
  const isActive = a => a.is_active !== false
  const nOf = a => (counts && counts[a.id] != null ? counts[a.id] : null)
  // Busiest first when counts are in — the facet then answers "who is producing" at a
  // glance, which is the question an admin opens it with. Alphabetical until they land, so
  // the list never reshuffles under a click mid-load.
  const order = useMemo(() => {
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '')
    const rank = (a, b) => counts ? ((nOf(b) || 0) - (nOf(a) || 0)) || byName(a, b) : byName(a, b)
    return { act: agents.filter(isActive).sort(rank), inact: agents.filter(a => !isActive(a)).sort(rank) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, counts])
  const needle = agentSearchKey(q.trim())
  const hit = a => agentSearchKey(a.name).includes(needle)
  const searching = q.trim().length > 0
  const actShown = searching ? order.act.filter(hit)
    : (showAll ? order.act : order.act.filter((a, i) => i < AGENT_FACET_TOP_N || selected.includes(a.id)))
  // Inactive appear when searched for by name, when the option is on, or when already ticked
  // (a selection must never become invisible).
  const inactShown = searching ? order.inact.filter(hit)
    : (withInactive ? order.inact : order.inact.filter(a => selected.includes(a.id)))
  const row = (a, muted) => (
    <FacetCheck key={a.id} checked={selected.includes(a.id)} excluded={excluded.includes(a.id)} onChange={() => onToggle(a.id)} onName={onName ? () => onName(a.id) : undefined}
      label={muted ? `${a.name} ·` : a.name} count={nOf(a)} />
  )
  return (
    <div className="space-y-1.5">
      {(order.act.length + order.inact.length) > AGENT_FACET_TOP_N && (
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('desk_facet_agent_search')}
          className="w-full h-7 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
      )}
      <div className={(showAll || searching || withInactive) ? 'max-h-64 overflow-y-auto space-y-1.5 pr-1' : 'space-y-1.5'}>
        {actShown.map(a => row(a, false))}
        {inactShown.length > 0 && (
          <p className="pt-1 text-xs uppercase tracking-wide text-muted-foreground">{t('desk_facet_agent_inactive')}</p>
        )}
        {inactShown.map(a => row(a, true))}
        {!actShown.length && !inactShown.length && (
          <p className="text-xs text-muted-foreground">{t('desk_facet_agent_none')}</p>
        )}
      </div>
      {!searching && order.act.length > AGENT_FACET_TOP_N && (
        <button type="button" className="text-xs text-primary hover:underline block"
          onClick={() => setShowAll(v => !v)}>
          {showAll ? t('desk_facet_show_less') : tLog('desk_facet_see_all', { n: order.act.length })}
        </button>
      )}
      {!searching && order.inact.length > 0 && (
        <button type="button" className="text-xs text-muted-foreground hover:text-foreground hover:underline block"
          onClick={() => setWithInactive(v => !v)}>
          {withInactive ? t('desk_facet_agent_hide_inactive') : tLog('desk_facet_agent_show_inactive', { n: order.inact.length })}
        </button>
      )}
      {selected.length > 0 && (
        <button type="button" className="text-xs text-destructive hover:underline block"
          onClick={() => onToggle(null)}>{t('filter_clear')}</button>
      )}
    </div>
  )
}
